// src/components/messaging/MessagingPanel.tsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Alert, Drawer, Modal, Select, Space, Typography, Button, Empty, Spin, Tag } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { isPatientSession } from "../../utils/userPrefs/validatePermissions";
import { useMessaging } from "../../hooks/communications/useMessaging";
import { getQueueConversations, createPatientQueueConversation, findOrCreateDirectConversation, claimConversation, addParticipant, removeParticipant, closeConversation } from "../../api/messaging";
import { Conversation } from "../../types/messaging";
import ConversationList from "./ConversationList";
import ConversationThread from "./ConversationThread";
import { useNotifications } from "../../context/NotificationContext";
import { fetchPatients } from "../../api/healthcare/PatientApi";
import { fetchTeamMembers } from "../../api/admin/teamApi";
import { useMessagingPanel } from "../../context/MessagingContext";
import type { UserUuid } from "../../types/identity";

const { Text, Title } = Typography;

interface MessagingPanelProps {
	open: boolean;
	onClose: () => void;
}

const MessagingPanel: React.FC<MessagingPanelProps> = ({ open, onClose }) => {
	const { showError, showSuccess } = useNotifications();
	const { highlightConversationId, clearHighlight } = useMessagingPanel();
	const isPatient = isPatientSession();

	const {
		conversations,
		activeConversationId,
		messages,
		typingUsers,
		connectionState,
		isLoading,
		userId,
		setActiveConversationId,
		loadConversations,
		loadMessages,
		sendNewMessage,
		markAsRead,
		subscribeToConversation,
		unsubscribeFromConversation,
		totalUnreadCount,
		queueCount,
	} = useMessaging({ autoConnect: true, autoLoadConversations: true });

	const [queueConversations, setQueueConversations] = useState<Conversation[]>([]);
	const [isLoadingQueue, setIsLoadingQueue] = useState(false);
	const [showClaimedNotice, setShowClaimedNotice] = useState(false);
	const [showNewConversationModal, setShowNewConversationModal] = useState(false);
	const [selectedPatientId, setSelectedPatientId] = useState<UserUuid | null>(null);
	const [patients, setPatients] = useState<any[]>([]);
	const [isSearchingPatients, setIsSearchingPatients] = useState(false);
	const [isCreatingConversation, setIsCreatingConversation] = useState(false);
	const [isMobile, setIsMobile] = useState(false);
	const patientSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Add practitioner modal state
	const [showAddPractitionerModal, setShowAddPractitionerModal] = useState(false);
	const [practitioners, setPractitioners] = useState<any[]>([]);
	const [selectedPractitionerId, setSelectedPractitionerId] = useState<UserUuid | null>(null);
	const [isSearchingPractitioners, setIsSearchingPractitioners] = useState(false);
	const [isAddingParticipant, setIsAddingParticipant] = useState(false);
	const practitionerSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		const checkMobile = () => setIsMobile(window.innerWidth < 768);
		checkMobile();
		window.addEventListener("resize", checkMobile);
		return () => window.removeEventListener("resize", checkMobile);
	}, []);

	const loadQueue = useCallback(async (): Promise<Conversation[] | null> => {
		if (isPatient) return null;

		setIsLoadingQueue(true);
		try {
			const queue = await getQueueConversations();
			setQueueConversations(queue);
			return queue;
		} catch (error) {
			console.error("Failed to load queue:", error);
			return null;
		} finally {
			setIsLoadingQueue(false);
		}
	}, [isPatient]);

	useEffect(() => {
		if (open && !isPatient) {
			loadQueue();
		}
		if (!open) setShowClaimedNotice(false);
	}, [open, isPatient, loadQueue]);

	// Fresh load per click-through: the panel's queue is not live, so a stale copy would misreport "claimed".
	useEffect(() => {
		if (!open || isPatient || !highlightConversationId) return;
		let cancelled = false;
		setShowClaimedNotice(false);
		loadQueue().then((queue) => {
			if (cancelled || !queue) return;
			if (!queue.some((c) => c.id === highlightConversationId)) {
				setShowClaimedNotice(true);
				clearHighlight();
			}
		});
		return () => {
			cancelled = true;
		};
	}, [open, isPatient, highlightConversationId, loadQueue, clearHighlight]);

	const activeConversation = conversations.find((c) => c.id === activeConversationId);
	const activeMessages = activeConversationId ? messages[activeConversationId] || [] : [];
	const activeTypingUsers = activeConversationId ? typingUsers[activeConversationId] || [] : [];

	const handleSelectConversation = async (conversationId: string) => {
		const conv = [...conversations, ...queueConversations].find((c) => c.id === conversationId);
		if (!conv) return;

		if (conv.assignment?.status === "unassigned" && !isPatient) {
			try {
				await claimConversation(conversationId, { practitioner_user_id: userId });
				showSuccess("Conversation claimed successfully");
				// Subscribe immediately after claim so no messages are missed while
				// the list refreshes. Fire refreshes without awaiting.
				subscribeToConversation(conversationId);
				loadConversations();
				loadQueue();
			} catch (error) {
				showError("Failed to claim conversation");
				return;
			}
		} else {
			subscribeToConversation(conversationId);
		}

		setActiveConversationId(conversationId);
		await loadMessages(conversationId);
		await markAsRead(conversationId);
	};

	const handleSendMessage = async (content: string, files?: File[]) => {
		if (!activeConversationId) return;

		try {
			await sendNewMessage(activeConversationId, content, files);
		} catch (error) {
			showError("Failed to send message");
			throw error;
		}
	};
	const handleStartConversation = async () => {
		if (isPatient) {
			if (!userId) return;

			setIsCreatingConversation(true);
			try {
				const conversation = await createPatientQueueConversation({
					patient_user_id: userId,
				});
				await loadConversations();
				setActiveConversationId(conversation.id);
				subscribeToConversation(conversation.id);
				showSuccess("Conversation started");
			} catch (error) {
				showError("Failed to start conversation");
			} finally {
				setIsCreatingConversation(false);
			}
		} else {
			setShowNewConversationModal(true);
		}
	};

	const handleCreateDirectConversation = async () => {
		console.log("Creating direct conversation with patient ID:", selectedPatientId, "for user ID:", userId);
		if (!selectedPatientId || !userId) return;

		setIsCreatingConversation(true);
		try {
			const conversation = await findOrCreateDirectConversation(userId, selectedPatientId);
			await loadConversations();
			setActiveConversationId(conversation.id);
			subscribeToConversation(conversation.id);
			setShowNewConversationModal(false);
			setSelectedPatientId(null);
			showSuccess("Conversation started");
		} catch (error) {
			showError("Failed to create conversation");
		} finally {
			setIsCreatingConversation(false);
		}
	};

	const handleBack = () => {
		if (activeConversationId) {
			unsubscribeFromConversation(activeConversationId);
		}
		setActiveConversationId(null);
	};

	const loadPatients = useCallback(async (search?: string) => {
		setIsSearchingPatients(true);
		try {
			const { data } = await fetchPatients(1, 30, search, "created_at", "desc", true);
			setPatients(data);
		} catch (err) {
			console.error("Error fetching patients:", err);
		} finally {
			setIsSearchingPatients(false);
		}
	}, []);

	const handlePatientSearch = useCallback((value: string) => {
		if (patientSearchTimer.current) clearTimeout(patientSearchTimer.current);
		patientSearchTimer.current = setTimeout(() => loadPatients(value || undefined), 300);
	}, [loadPatients]);

	const loadPractitioners = useCallback(async (search?: string) => {
		setIsSearchingPractitioners(true);
		try {
			const { data } = await fetchTeamMembers(1, 30, search);
			// Exclude people already in the conversation
			const existingIds = new Set((activeConversation?.participants || []).map((p) => p.user_id));
			setPractitioners(data.filter((p: any) => !existingIds.has(p.id)));
		} catch (err) {
			console.error("Error fetching practitioners:", err);
		} finally {
			setIsSearchingPractitioners(false);
		}
	}, [activeConversation]);

	const handlePractitionerSearch = useCallback((value: string) => {
		if (practitionerSearchTimer.current) clearTimeout(practitionerSearchTimer.current);
		practitionerSearchTimer.current = setTimeout(() => loadPractitioners(value || undefined), 300);
	}, [loadPractitioners]);

	const handleOpenAddPractitioner = useCallback(() => {
		setSelectedPractitionerId(null);
		setPractitioners([]);
		setShowAddPractitionerModal(true);
		loadPractitioners();
	}, [loadPractitioners]);

	const handleAddPractitioner = async () => {
		if (!selectedPractitionerId || !activeConversationId || !userId) return;
		setIsAddingParticipant(true);
		try {
			await addParticipant(activeConversationId, {
				new_user_id: selectedPractitionerId,
				added_by_user_id: userId,
				role: 'practitioner',
			});
			await loadConversations();
			setShowAddPractitionerModal(false);
			setSelectedPractitionerId(null);
			showSuccess("Practitioner added to conversation");
		} catch (error) {
			showError("Failed to add practitioner");
		} finally {
			setIsAddingParticipant(false);
		}
	};

	const handleRemoveParticipant = useCallback(async (targetUserId: UserUuid) => {
		if (!activeConversationId || !userId) return;
		try {
			await removeParticipant(activeConversationId, {
				user_id: targetUserId,
				removed_by_user_id: userId,
			});
			await loadConversations();
			showSuccess("Participant removed");
		} catch (error) {
			showError("Failed to remove participant");
		}
	}, [activeConversationId, userId, loadConversations, showSuccess, showError]);

	const handleCloseConversation = useCallback(async () => {
		if (!activeConversationId || !userId) return;
		try {
			await closeConversation(activeConversationId, { user_id: userId });
			await loadConversations();
			setActiveConversationId(null);
			showSuccess("Conversation closed successfully");
		} catch (error) {
			showError("Failed to close conversation");
			throw error;
		}
	}, [activeConversationId, userId, loadConversations, setActiveConversationId, showSuccess, showError]);

	const showConversationList = !activeConversationId || !isMobile;
	const showConversationThread = activeConversationId && (!isMobile || !showConversationList);

	return (
		<>
			<Drawer
				data-testid="messaging-drawer"
				title={
					<Space className="w-full justify-between">
						<Space>
							<Title level={5} className="!mb-0">
								Messages
							</Title>
							<Tag color={connectionState === "connected" ? "success" : connectionState === "connecting" ? "warning" : "error"}>{connectionState}</Tag>
						</Space>
					</Space>
				}
				placement="right"
				onClose={onClose}
				open={open}
				width={isMobile ? "100%" : 800}
				className="messaging-drawer"
				closeIcon={<CloseOutlined />}
				forceRender
			>
				<div className="messaging-panel">
					{isLoading ? (
					<div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
							<Spin size="large" />
						</div>
					) : conversations.length === 0 && queueConversations.length === 0 && !activeConversationId && !showClaimedNotice ? (
						<div className="messaging-empty-state">
							<Empty
								description={
									<Space direction="vertical" align="center">
										<Text type="secondary">{isPatient ? "You don't have any conversations yet" : "No conversations available"}</Text>
										<Button data-testid="messaging-new-btn" type="primary" onClick={handleStartConversation} loading={isCreatingConversation}>
											{isPatient ? "Start Conversation" : "New Conversation"}
										</Button>
									</Space>
								}
							/>
						</div>
					) : (
						<div className="messaging-content">
							{showConversationList && (
								<div className="messaging-sidebar">
									<ConversationList
										conversations={conversations}
										queueConversations={queueConversations}
										activeConversationId={activeConversationId}
										onSelectConversation={handleSelectConversation}
										currentUserId={userId}
										isPatient={isPatient}
										onNewConversation={handleStartConversation}
										notice={
											showClaimedNotice && (
												<Alert
													data-testid="messaging-queue-claimed-notice"
													type="info"
													showIcon
													closable
													message="This request has already been claimed."
													onClose={() => setShowClaimedNotice(false)}
												/>
											)
										}
									/>
								</div>
							)}

							{showConversationThread && activeConversation && (
								<div className="messaging-main">
								<ConversationThread
									conversation={activeConversation}
									messages={activeMessages}
									typingUsers={activeTypingUsers}
									currentUserId={userId}
									onSendMessage={handleSendMessage}
									onBack={isMobile ? handleBack : undefined}
									isPatient={isPatient}
									onAddParticipant={isPatient ? undefined : handleOpenAddPractitioner}
									onRemoveParticipant={isPatient ? undefined : handleRemoveParticipant}
									onCloseConversation={isPatient ? undefined : handleCloseConversation}
								/>
								</div>
							)}
						</div>
					)}
				</div>
			</Drawer>

			<Modal
				title="Add Practitioner"
				open={showAddPractitionerModal}
				onCancel={() => {
					setShowAddPractitionerModal(false);
					setSelectedPractitionerId(null);
				}}
				onOk={handleAddPractitioner}
				confirmLoading={isAddingParticipant}
				okButtonProps={{ disabled: !selectedPractitionerId }}
				okText="Add to Conversation"
			>
			<Space direction="vertical" style={{ width: "100%" }} size="large">
					<Text>Select a practitioner to add to this conversation:</Text>
					<Select
						showSearch
						placeholder="Search for a practitioner"
						style={{ width: "100%" }}
						value={selectedPractitionerId}
						onChange={setSelectedPractitionerId}
						onFocus={() => loadPractitioners()}
						onSearch={handlePractitionerSearch}
						filterOption={false}
						loading={isSearchingPractitioners}
						notFoundContent={isSearchingPractitioners ? <Spin size="small" /> : "No practitioners found"}
						options={practitioners.map((p) => ({
							value: p.id,
							label: `${p.first_name} ${p.last_name}${p.email ? ` — ${p.email}` : ""}`,
						}))}
					/>
				</Space>
			</Modal>

			<Modal
				title="New Conversation"
				open={showNewConversationModal}
				onCancel={() => {
					setShowNewConversationModal(false);
					setSelectedPatientId(null);
				}}
				onOk={handleCreateDirectConversation}
				confirmLoading={isCreatingConversation}
				okButtonProps={{ disabled: !selectedPatientId }}
			>
			<Space direction="vertical" style={{ width: "100%" }} size="large">
					<Text>Select a patient to start a conversation with:</Text>
					<Select
						showSearch
						placeholder="Search name, email or phone…"
						style={{ width: "100%" }}
						value={selectedPatientId}
						onChange={setSelectedPatientId}
						onFocus={() => loadPatients()}
					onSearch={handlePatientSearch}
					filterOption={false}
					loading={isSearchingPatients}
					notFoundContent={isSearchingPatients ? <Spin size="small" /> : "No patients found"}
						options={patients.map((p) => ({
							value: p.id,
							// Phone is shown so a match found BY phone can be verified — the point of
							// searching by number (86d3nzmg1). Blank phones render nothing, not a
							// dangling separator: `phone` is typed non-nullable but is "" on real rows.
							label: `${p.first_name} ${p.last_name} - ${p.email}${p.phone ? ` - ${p.phone}` : ""}`,
						}))}
					/>
				</Space>
			</Modal>
		</>
	);
};

export default MessagingPanel;
